@file:OptIn(kotlin.uuid.ExperimentalUuidApi::class)

package com.yogaaicoach.pages.workout

import com.yogaaicoach.common.ui.components.ChevronDownIcon
import com.yogaaicoach.common.ui.components.InfoIconPrimary
import com.yogaaicoach.common.ui.components.TopBar
import com.yogaaicoach.pages.trainingprogram.Duration
import com.yogaaicoach.pages.workout.data.models.FocusAreaDataNarrowed
import com.yogaaicoach.scope
import com.yogaaicoach.user.AppViewModel
import kotlinx.coroutines.launch
import react.ChildrenBuilder
import react.FC
import react.Props
import react.dom.html.ReactHTML.button
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.h2
import react.dom.html.ReactHTML.option
import react.dom.html.ReactHTML.p
import react.dom.html.ReactHTML.select
import react.dom.html.ReactHTML.span
import react.useEffectOnce
import react.useState
import web.cssom.ClassName
import web.events.EventHandler
import web.html.ButtonType
import web.html.button as buttonType
import web.location.location
import web.window.window
import kotlin.uuid.Uuid

private const val EditWorkoutHash = "#edit-workout"

internal external interface EditWorkoutPageProps : Props {
    var viewModel: EditWorkoutViewModel
    var appViewModel: AppViewModel
    var workoutGuid: String
    var defaultDuration: Int
    var navigateTo: (String) -> Unit
    var onSaveDone: (Uuid, Int) -> Unit
    var onNavigateToPayment: () -> Unit
    var onNavigateToContactUs: () -> Unit
}

internal val EditWorkoutPage = FC<EditWorkoutPageProps> { props ->
    val vm = props.viewModel
    val guid = Uuid.parse(props.workoutGuid)
    vm.init(guid, props.defaultDuration)

    var isPageLoaded by useState(vm.isPageLoaded.value)
    var selectedDuration by useState(vm.selectedDuration.value)
    var selectedFocusAreas by useState(vm.selectedFocusAreas.value)
    var allFocusAreas by useState(vm.allFocusAreas.value)
    var focusAreaErrorText by useState(vm.focusAreaErrorText.value)
    var durationErrorText by useState(vm.durationErrorText.value)
    var showQuitDialog by useState(false)
    var selectedFocusAreaDetail by useState<FocusAreaDataNarrowed?>(null)

    useEffectOnce {
        scope.launch { vm.isPageLoaded.collect { isPageLoaded = it } }
        scope.launch { vm.selectedDuration.collect { selectedDuration = it } }
        scope.launch { vm.selectedFocusAreas.collect { selectedFocusAreas = it } }
        scope.launch { vm.allFocusAreas.collect { allFocusAreas = it } }
        scope.launch { vm.focusAreaErrorText.collect { focusAreaErrorText = it } }
        scope.launch { vm.durationErrorText.collect { durationErrorText = it } }

        window.onpopstate = EventHandler {
            if (!location.hash.startsWith(EditWorkoutHash) && vm.hasChanges.value) {
                location.hash = EditWorkoutHash.removePrefix("#")
                showQuitDialog = true
            }
        }
    }

    selectedFocusAreaDetail?.let { focusArea ->
        FocusAreaDetailDialog(focusArea) { selectedFocusAreaDetail = null }
    }

    if (showQuitDialog) {
        UnsavedChangesDialog(
            onLeave = { showQuitDialog = false; vm.reset(); props.navigateTo("#workout") },
            onStay = { showQuitDialog = false },
        )
    }

    TopBar {
        title = "Edit Workout"
        onSignOut = { props.appViewModel.signOut() }
        onNavigateToDashboard = { props.navigateTo("") }
        onNavigateToPayment = props.onNavigateToPayment
        onNavigateToContactUs = props.onNavigateToContactUs
    }

    if (!isPageLoaded) {
        div { className = ClassName("loading-screen") }
        return@FC
    }

    div {
        className = ClassName("edit-workout-content")

        EditSectionCard(title = "Duration", errorText = durationErrorText) {
            div {
                className = ClassName("duration-segmented-row")
                Duration.entries.forEachIndexed { i, duration ->
                    button {
                        type = ButtonType.buttonType
                        className = ClassName(buildList {
                            add("duration-segment")
                            if (selectedDuration == duration) add("duration-segment-selected")
                            if (i == 0) add("duration-segment-first")
                            if (i == Duration.entries.lastIndex) add("duration-segment-last")
                        }.joinToString(" "))
                        onClick = { vm.updateSelectedDuration(duration) }
                        +"${duration.minutes} min"
                    }
                }
            }
        }

        EditSectionCard(title = "Focus Areas", errorText = focusAreaErrorText) {
            selectedFocusAreas.forEachIndexed { index, focusArea ->
                div {
                    className = ClassName("edit-workout-select-row")
                    div {
                        className = ClassName("edit-workout-select-wrapper")
                        span {
                            className = ClassName(if (focusArea != null) "edit-workout-select-value" else "edit-workout-select-value edit-workout-select-placeholder")
                            +(focusArea?.name ?: "Select focus area ${index + 1}")
                        }
                        ChevronDownIcon {}
                        select {
                            className = ClassName("edit-workout-select-native")
                            value = focusArea?.guid?.toString() ?: ""
                            onChange = { event ->
                                val guidStr = event.target.value
                                val selected = allFocusAreas.find { it.guid.toString() == guidStr }
                                if (selected != null) vm.selectFocusArea(selected, index)
                            }
                            option { value = ""; disabled = true; +"Select focus area ${index + 1}" }
                            allFocusAreas.forEach { area ->
                                option {
                                    value = area.guid.toString()
                                    +area.name
                                }
                            }
                        }
                    }
                    if (focusArea != null) {
                        button {
                            type = ButtonType.buttonType
                            className = ClassName("edit-workout-info-btn")
                            onClick = { selectedFocusAreaDetail = focusArea }
                            InfoIconPrimary {}
                        }
                    } else {
                        div { className = ClassName("edit-workout-info-placeholder") }
                    }
                }
            }
        }
    }

    div {
        className = ClassName("edit-workout-footer")
        button {
            className = ClassName("primary-button")
            onClick = { vm.saveChanges(guid) { workoutGuid, durationMinutes ->
                vm.reset()
                props.onSaveDone(workoutGuid, durationMinutes)
            }}
            span { +"Save Changes" }
        }
    }
}

private fun ChildrenBuilder.EditSectionCard(
    title: String,
    errorText: String?,
    content: ChildrenBuilder.() -> Unit,
) {
    div {
        className = ClassName("edit-workout-card")
        h2 { className = ClassName("edit-workout-card-title"); +title }
        if (errorText != null) {
            p { className = ClassName("edit-workout-error"); +errorText }
        }
        content()
    }
}

private fun ChildrenBuilder.FocusAreaDetailDialog(
    focusArea: FocusAreaDataNarrowed,
    onDismiss: () -> Unit,
) {
    div {
        className = ClassName("dialog-backdrop")
        onClick = { onDismiss() }
        div {
            className = ClassName("dialog-box")
            onClick = { event -> event.stopPropagation() }
            h2 { className = ClassName("dialog-title"); +focusArea.name }
            p { className = ClassName("dialog-body"); +focusArea.focus }
            if (focusArea.benefits.isNotBlank()) {
                p { className = ClassName("workout-focus-dialog-benefits-label"); +"Benefits" }
                p { className = ClassName("dialog-body"); +focusArea.benefits }
            }
            div {
                className = ClassName("dialog-button-row")
                button {
                    type = ButtonType.buttonType
                    className = ClassName("text-button")
                    +"Close"
                    onClick = { onDismiss() }
                }
            }
        }
    }
}

private fun ChildrenBuilder.UnsavedChangesDialog(onLeave: () -> Unit, onStay: () -> Unit) {
    div {
        className = ClassName("dialog-backdrop")
        onClick = { onStay() }
        div {
            className = ClassName("dialog-box")
            onClick = { event -> event.stopPropagation() }
            h2 { className = ClassName("dialog-title"); +"Unsaved changes" }
            p { className = ClassName("dialog-body"); +"You have unsaved changes. Are you sure you want to leave this page?" }
            div {
                className = ClassName("dialog-button-row")
                button { className = ClassName("text-button"); +"Stay"; onClick = { onStay() } }
                button { className = ClassName("text-button"); +"Leave"; onClick = { onLeave() } }
            }
        }
    }
}
